import { isDefined } from "@clipboard-health/util-ts";
import { type Shift } from "@src/interface";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { checkIsShiftRequestingReplacement } from "../../Shifts/utils/checkIsShiftRequestingReplacement";
import { type ShiftChip, ShiftChipTypes } from "./types";
import {
  isShiftNoLongerBookable,
  shouldShowStillBookableChip,
  shouldShowUrgentChip,
  shouldShowWorkerLeftEarlyRequestShiftChip,
  shouldShowWorkerLeftEarlyShiftChip,
} from "./utils";

interface UseShiftChipsParams {
  shift: Pick<
    Shift,
    | "_id"
    | "agentId"
    | "agentReq"
    | "deleted"
    | "end"
    | "extendBookabilityTo"
    | "isRequestToBook"
    | "name"
    | "replacedShiftId"
    | "start"
    | "urgency"
    | "workerLeftEarlyRequest"
  > & {
    facility?: {
      tmz?: string;
    };
  };
  workplaceInstantBook?: boolean | string;
  workplaceRequestToBookConfig?: {
    qualifications?: string[];
    shiftTimeSlots?: string[];
    autoApprovePreviousApprovedWorkers?: boolean;
  };
  hasPendingBookingRequests?: boolean;
  pendingBookingRequestCount?: number;
  onViewRequestsClick?: () => void;
  isRequestToBookPerShiftFeatureFlagEnabled?: boolean;
}

function isShiftRequestToBook(params: {
  shiftIsRequestToBook?: boolean;
  workplaceRequestToBookConfig?: {
    qualifications?: string[];
    shiftTimeSlots?: string[];
    autoApprovePreviousApprovedWorkers?: boolean;
  };
  shiftQualification: string;
  shiftTimeSlot?: string;
}): boolean {
  const { shiftIsRequestToBook, workplaceRequestToBookConfig, shiftQualification, shiftTimeSlot } =
    params;

  if (isDefined(shiftIsRequestToBook)) {
    return shiftIsRequestToBook;
  }

  if (!workplaceRequestToBookConfig) {
    return true;
  }

  const { qualifications = [], shiftTimeSlots = [] } = workplaceRequestToBookConfig;
  const hasNoFilters = qualifications.length === 0 && shiftTimeSlots.length === 0;

  if (hasNoFilters) {
    return true;
  }

  const qualificationMatches = qualifications.includes(shiftQualification);
  const timeSlotMatches = shiftTimeSlot ? shiftTimeSlots.includes(shiftTimeSlot) : false;

  return qualificationMatches || timeSlotMatches;
}

function getRequestToBookChip(params: {
  shiftIsRequestToBookValue: boolean;
  hasPendingBookingRequests: boolean;
  pendingBookingRequestCount: number;
  onViewRequestsClick?: () => void;
}): ShiftChip {
  const {
    shiftIsRequestToBookValue,
    hasPendingBookingRequests,
    pendingBookingRequestCount,
    onViewRequestsClick,
  } = params;

  if (!shiftIsRequestToBookValue) {
    return {
      type: ShiftChipTypes.INSTANT_BOOK,
    };
  }

  if (hasPendingBookingRequests && pendingBookingRequestCount > 0) {
    const requestLabel = `${pendingBookingRequestCount} Pending Request${
      pendingBookingRequestCount === 1 ? "" : "s"
    }`;
    return {
      type: ShiftChipTypes.PENDING_REQUESTS,
      label: requestLabel,
      additionalData: {
        pendingRequestCount: pendingBookingRequestCount,
      },
      handleClick: onViewRequestsClick,
    };
  }

  return {
    type: ShiftChipTypes.AWAITING_REQUESTS,
  };
}

export function useShiftChips(params: UseShiftChipsParams): ShiftChip[] {
  const {
    shift,
    workplaceInstantBook,
    workplaceRequestToBookConfig,
    hasPendingBookingRequests = false,
    pendingBookingRequestCount = 0,
    onViewRequestsClick,
    isRequestToBookPerShiftFeatureFlagEnabled = false,
  } = params;

  return useMemo(() => {
    const chips: ShiftChip[] = [];
    const workerId = shift.agentId;
    const isRequestingReplacement = checkIsShiftRequestingReplacement(shift);
    const isStillBookable = !isShiftNoLongerBookable(shift);

    if (shouldShowUrgentChip({ shift })) {
      chips.push({ type: ShiftChipTypes.URGENT });
    }

    if (shouldShowStillBookableChip(shift)) {
      chips.push({
        type: ShiftChipTypes.STILL_BOOKABLE,
        additionalData: {
          extendedBookabilityTo: parseISO(shift.extendBookabilityTo),
          facilityTimeZone: shift.facility?.tmz,
        },
      });
    }

    if (shouldShowWorkerLeftEarlyRequestShiftChip(shift)) {
      chips.push({
        type: ShiftChipTypes.WORKER_LEFT_EARLY_REPLACEMENT_WORKER,
      });
    }

    if (shouldShowWorkerLeftEarlyShiftChip(shift)) {
      chips.push({
        type: ShiftChipTypes.WORKER_LEFT_EARLY,
      });
    }

    const workplaceHasRequestToBook = workplaceInstantBook === "REQUEST_TO_BOOK";
    const shouldShowRequestToBookChips =
      workplaceHasRequestToBook && isRequestToBookPerShiftFeatureFlagEnabled;
    const isEffectivelyUnfilled = !workerId || isRequestingReplacement;

    if (shouldShowRequestToBookChips && isEffectivelyUnfilled && isStillBookable) {
      const shiftIsRequestToBookValue = isShiftRequestToBook({
        shiftIsRequestToBook: shift.isRequestToBook,
        workplaceRequestToBookConfig,
        shiftQualification: shift.agentReq,
        shiftTimeSlot: shift.name,
      });

      chips.push(
        getRequestToBookChip({
          shiftIsRequestToBookValue,
          hasPendingBookingRequests,
          pendingBookingRequestCount,
          onViewRequestsClick,
        })
      );
    }

    return chips;
  }, [
    shift,
    workplaceInstantBook,
    workplaceRequestToBookConfig,
    hasPendingBookingRequests,
    pendingBookingRequestCount,
    onViewRequestsClick,
    isRequestToBookPerShiftFeatureFlagEnabled,
  ]);
}
